import React, { useState } from 'react';
import './App.css';

function App() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMenuOpen(false); // Close menu after navigation
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage('');

    try {
      // Create mailto link with form data
      const subject = encodeURIComponent(`Website Contact Form: ${formData.name}`);
      const body = encodeURIComponent(
        `Name: ${formData.name}\n` +
        `Email: ${formData.email}\n` +
        `Phone: ${formData.phone}\n\n` +
        `Message:\n${formData.message}`
      );
      
      const mailtoUrl = `mailto:info@dandcplumbing.co.uk?subject=${subject}&body=${body}`;
      window.location.href = mailtoUrl;
      
      setSubmitMessage('Email client opened! Please send the email to complete your message.');
      setFormData({ name: '', email: '', phone: '', message: '' });
    } catch (error) {
      setSubmitMessage('There was an error. Please call us directly at 07438312107.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="App">
      {/* Header */}
      <header style={headerStyle}>
        <nav style={navStyle}>
          <div style={logoContainerStyle}>
            <img src="/DandCLogo.png" alt="D&C Plumbing" style={logoStyle} className="logo-style" />
          </div>
          
          {/* Desktop Navigation */}
          <ul style={navListStyle} className="desktop-nav">
            <li><a href="#hero" style={navLinkStyle}>Home</a></li>
            <li><a href="#services" style={navLinkStyle}>Services</a></li>
            <li><a href="#projects" style={navLinkStyle}>Recent Projects</a></li>
            <li><a href="#pricing" style={navLinkStyle}>Fixed Prices</a></li>
            <li><a href="#about" style={navLinkStyle}>About</a></li>
            <li><a href="#contact" style={navLinkStyle}>Contact</a></li>
          </ul>
          
          {/* Hamburger Menu Button */}
          <button 
            className="hamburger-menu"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            style={hamburgerStyle}
            aria-label="Toggle navigation menu"
          >
            <span style={hamburgerLineStyle}></span>
            <span style={hamburgerLineStyle}></span>
            <span style={hamburgerLineStyle}></span>
          </button>
        </nav>
        
        {/* Mobile Menu Overlay */}
        {isMenuOpen && (
          <div className="mobile-menu-overlay" style={mobileMenuOverlayStyle}>
            <ul style={mobileMenuListStyle}>
              <li><a href="#hero" style={mobileMenuLinkStyle} onClick={() => scrollToSection('hero')}>Home</a></li>
              <li><a href="#services" style={mobileMenuLinkStyle} onClick={() => scrollToSection('services')}>Services</a></li>
              <li><a href="#projects" style={mobileMenuLinkStyle} onClick={() => scrollToSection('projects')}>Recent Projects</a></li>
              <li><a href="#pricing" style={mobileMenuLinkStyle} onClick={() => scrollToSection('pricing')}>Fixed Prices</a></li>
              <li><a href="#about" style={mobileMenuLinkStyle} onClick={() => scrollToSection('about')}>About</a></li>
              <li><a href="#contact" style={mobileMenuLinkStyle} onClick={() => scrollToSection('contact')}>Contact</a></li>
            </ul>
          </div>
        )}
      </header>

      {/* Hero Section */}
      <section id="hero" style={heroStyle}>
        <div style={heroContentStyle}>
          <h1 style={heroTitleStyle}>Professional Plumbers Stockport & Manchester</h1>
          <p style={heroSubtitleStyle}>
            Time served plumbers serving Stockport, Manchester and Greater Manchester. Emergency repairs, bathroom installations, fixed price repairs.
          </p>
          <div style={heroButtonsStyle}>
            <button style={primaryButtonStyle} onClick={() => scrollToSection('contact-form')}>
              Get Free Quote
            </button>
            <button style={secondaryButtonStyle} onClick={() => scrollToSection('services')}>
              Our Services
            </button>
          </div>
        </div>
      </section>

      {/* Contact Info Bar */}
      <section style={contactBarStyle} itemScope itemType="https://schema.org/LocalBusiness">
        <div style={contactItemStyle}>
          <span style={contactIconStyle}>📞</span>
          <div>
            <h3 style={contactTitleStyle}>Emergency Plumber</h3>
            <p style={contactTextStyle}>
              <a href="tel:07438312107" itemProp="telephone" style={{color: '#333', textDecoration: 'none'}}>
                07438312107
              </a>
            </p>
          </div>
        </div>
        <div style={contactItemStyle}>
          <span style={contactIconStyle}>✉️</span>
          <div>
            <h3 style={contactTitleStyle}>Email Us</h3>
            <p style={contactTextStyle}>
              <a href="mailto:info@dandcplumbing.co.uk" itemProp="email" style={{color: '#333', textDecoration: 'none'}}>
                info@dandcplumbing.co.uk
              </a>
            </p>
          </div>
        </div>
        <div style={contactItemStyle}>
          <span style={contactIconStyle}>📍</span>
          <div>
            <h3 style={contactTitleStyle}>Service Areas</h3>
            <p style={contactTextStyle} itemProp="areaServed">Stockport, Manchester, Heaton Mersey, Didsbury</p>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" style={servicesStyle}>
        <h2 style={sectionTitleStyle}>Plumbing Services Stockport & Manchester</h2>
        <div style={servicesGridStyle}>
          <div style={serviceCardStyle}>
            <div style={serviceIconStyle}>🚿</div>
            <h3 style={serviceCardTitleStyle}>Bathroom Plumbing</h3>
            <p style={serviceCardTextStyle}>Complete bathroom installations and repairs</p>
          </div>
          <div style={serviceCardStyle}>
            <div style={serviceIconStyle}>🏠</div>
            <h3 style={serviceCardTitleStyle}>Kitchen Plumbing</h3>
            <p style={serviceCardTextStyle}>Kitchen installations and maintenance</p>
          </div>
          <div style={serviceCardStyle}>
            <div style={serviceIconStyle}>🔧</div>
            <h3 style={serviceCardTitleStyle}>Emergency Repairs</h3>
            <p style={serviceCardTextStyle}>24/7 emergency plumbing services</p>
          </div>
          <div style={serviceCardStyle}>
            <div style={serviceIconStyle}>🔥</div>
            <h3 style={serviceCardTitleStyle}>Heating Systems</h3>
            <p style={serviceCardTextStyle}>Radiator fitting and heating maintenance</p>
          </div>
        </div>
      </section>

      {/* Recent Projects Section */}
      <section id="projects" style={projectsStyle}>
        <h2 style={sectionTitleStyle}>Recent Projects</h2>
        <div style={projectsGridStyle}>
          <article style={projectCardStyle}>
            <img src="/waterpf.jpg" alt="Professional water pipe installation by D&C Plumbing in Stockport - showing quality pipework and fittings" style={projectImageStyle} />
            <div style={projectContentStyle}>
              <h3 style={projectTitleStyle}>Water Pipe Installation</h3>
              <p style={projectDescStyle}>Professional water line installation and repair services throughout Stockport and Manchester</p>
            </div>
          </article>
          <article style={projectCardStyle}>
            <img src="/work.jpg" alt="D&C Plumbing professional at work - expert plumbing services in Manchester and Stockport" style={projectImageStyle} />
            <div style={projectContentStyle}>
              <h3 style={projectTitleStyle}>Professional Plumbing Work</h3>
              <p style={projectDescStyle}>Expert plumbing services for residential and commercial properties across Greater Manchester</p>
            </div>
          </article>
          <article style={projectCardStyle}>
            <img src="/tap.jpg" alt="Kitchen tap installation and repair by professional plumbers D&C Plumbing" style={projectImageStyle} />
            <div style={projectContentStyle}>
              <h3 style={projectTitleStyle}>Tap Installation & Repair</h3>
              <p style={projectDescStyle}>Quality tap fitting and repair services for kitchen and bathroom - fixed price from £55</p>
            </div>
          </article>
          <article style={projectCardStyle}>
            <img src="/shower.jpg" alt="Modern shower installation by D&C Plumbing - contemporary bathroom fitting in Stockport" style={projectImageStyle} />
            <div style={projectContentStyle}>
              <h3 style={projectTitleStyle}>Modern Shower Installation</h3>
              <p style={projectDescStyle}>Contemporary shower unit installation with thermostatic controls and professional tiling</p>
            </div>
          </article>
          <article style={projectCardStyle}>
            <img src="/bathroom.jpg" alt="Complete bathroom renovation and installation by D&C Plumbing - full suite and plumbing work" style={projectImageStyle} />
            <div style={projectContentStyle}>
              <h3 style={projectTitleStyle}>Complete Bathroom Installation</h3>
              <p style={projectDescStyle}>Full bathroom renovation including new suite, professional tiling, and complete plumbing system</p>
            </div>
          </article>
          <article style={projectCardStyle}>
            <img src="/basine2.jpg" alt="Basin and sink installation by qualified plumbers D&C Plumbing - quality bathroom fitting" style={projectImageStyle} />
            <div style={projectContentStyle}>
              <h3 style={projectTitleStyle}>Basin & Sink Installation</h3>
              <p style={projectDescStyle}>Professional basin and sink fitting with quality pipework and waste connections</p>
            </div>
          </article>
        </div>
      </section>

      {/* Fixed Price Repairs Section */}
      <section id="pricing" style={pricingStyle}>
        <h2 style={sectionTitleStyle}>Fixed Price Repairs</h2>
        <p style={pricingSubtitleStyle}>No hidden costs - you know exactly what you'll pay before we start</p>
        <div style={pricingGridStyle}>
          <div style={pricingCardStyle}>
            <div style={pricingIconStyle}>🚰</div>
            <h3 style={pricingTitleStyle}>Tap Repair/Replace</h3>
            <div style={priceStyle}>£55 - £95</div>
            <ul style={pricingListStyle}>
              <li>Diagnose tap issues</li>
              <li>Replace washers/cartridges</li>
              <li>Full tap replacement if needed</li>
              <li>All parts included</li>
            </ul>
          </div>
          <div style={pricingCardStyle}>
            <div style={pricingIconStyle}>🚽</div>
            <h3 style={pricingTitleStyle}>Toilet Repair</h3>
            <div style={priceStyle}>£65 - £105</div>
            <ul style={pricingListStyle}>
              <li>Fix running toilet</li>
              <li>Replace cistern parts</li>
              <li>Unblock toilet</li>
              <li>Seal replacements</li>
            </ul>
          </div>
          <div style={pricingCardStyle}>
            <div style={pricingIconStyle}>🔧</div>
            <h3 style={pricingTitleStyle}>Drain Unblocking</h3>
            <div style={priceStyle}>£75 - £130</div>
            <ul style={pricingListStyle}>
              <li>Kitchen sink drains</li>
              <li>Bathroom drains</li>
              <li>External drain clearing</li>
            </ul>
          </div>
          <div style={pricingCardStyle}>
            <div style={pricingIconStyle}>💧</div>
            <h3 style={pricingTitleStyle}>Leak Repairs</h3>
            <div style={priceStyle}>£85 - £160</div>
            <ul style={pricingListStyle}>
              <li>Find and fix leaks</li>
              <li>Pipe joint repairs</li>
              <li>Radiator leak fixes</li>
              <li>Emergency call-outs</li>
            </ul>
          </div>
          <div style={pricingCardStyle}>
            <div style={pricingIconStyle}>🔥</div>
            <h3 style={pricingTitleStyle}>Radiator Services</h3>
            <div style={priceStyle}>£95 - £190</div>
            <ul style={pricingListStyle}>
              <li>Radiator installation</li>
              <li>Bleeding radiators</li>
              <li>Valve replacements</li>
              <li>System balancing</li>
            </ul>
          </div>
          <div style={pricingCardStyle}>
            <div style={pricingIconStyle}>⚡</div>
            <h3 style={pricingTitleStyle}>Emergency Call-out</h3>
            <div style={priceStyle}>£105 + parts</div>
            <ul style={pricingListStyle}>
              <li>24/7 availability</li>
              <li>Same day service</li>
              <li>Burst pipes</li>
              <li>No water emergencies</li>
            </ul>
          </div>
        </div>
        <div style={pricingNoteStyle}>
          <p>*All prices include labour and basic parts. Additional charges may apply for specialist parts or complex installations.</p>
          <p><strong>6-month guarantee on all repairs</strong></p>
        </div>
      </section>

      {/* About Section */}
      <section id="about" style={aboutStyle}>
        <div style={aboutContentStyle}>
          <h2 style={sectionTitleStyle}>About D&C Plumbing</h2>
          <p style={aboutTextStyle}>
            We are your friendly local plumber covering the Heatons and nearby areas of Manchester and Stockport. 
            Whether you've got a blocked drain or a burst pipe, our team is on hand 7 days a week.
          </p>
          <p style={aboutTextStyle}>
            We offer a complete plumbing service including installations and repairs, making us your one-stop 
            shop for all of your plumbing needs. All our work comes with a 6 month guarantee.
          </p>
          <ul style={areaListStyle}>
            <li>Heaton Mersey</li>
            <li>Heaton Moor</li>
            <li>Heaton Chapel</li>
            <li>Didsbury</li>
            <li>Withington</li>
            <li>Burnage</li>
            <li>Cheadle</li>
            <li>Gatley</li>
            <li>Reddish</li>
          </ul>
        </div>
      </section>

      {/* Credentials Section */}
      <section style={credentialsStyle}>
        <h2 style={sectionTitleStyle}>Fully Qualified & Insured</h2>
        <p style={credentialsSubtitleStyle}>
          Your peace of mind is our priority. We are fully qualified and insured.
        </p>
        <div style={credentialsGridStyle}>
          <div style={credentialCardStyle}>
            <img src="/insurance.png" alt="Fully Insured" style={credentialLogoStyle} />
            <h3 style={credentialTitleStyle}>Fully Insured</h3>
            <p style={credentialDescStyle}>
              Comprehensive public liability insurance covers all our work. Complete protection for you and your property.
            </p>
          </div>
          <div style={credentialCardStyle}>
            <div style={{...credentialLogoStyle, background: 'linear-gradient(45deg, #667eea, #764ba2)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '3rem', borderRadius: '10px', margin: '0 auto 1.5rem auto'}}>
              ✓
            </div>
            <h3 style={credentialTitleStyle}>6 Month Guarantee</h3>
            <p style={credentialDescStyle}>
              All our work comes with a comprehensive 6-month guarantee. Quality workmanship you can trust.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section id="contact-form" style={contactFormSectionStyle}>
        <div style={contactFormContainerStyle}>
          <h2 style={{...sectionTitleStyle, color: 'white', WebkitTextFillColor: 'white', textShadow: '2px 2px 4px rgba(0,0,0,0.5)'}}>Get Your Free Quote</h2>
          <p style={contactFormSubtitleStyle}>
            Ready to get started? Send us a message and we'll get back to you within 24 hours.
          </p>
          
          <form onSubmit={handleSubmit} style={contactFormStyle} className="contact-form">
            <div style={formRowStyle} className="contact-form-row">
              <div style={formGroupStyle}>
                <label style={formLabelStyle}>Name *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  style={formInputStyle}
                  placeholder="Your full name"
                />
              </div>
              <div style={formGroupStyle}>
                <label style={formLabelStyle}>Phone *</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  required
                  style={formInputStyle}
                  placeholder="Your phone number"
                />
              </div>
            </div>
            
            <div style={formGroupStyle}>
              <label style={formLabelStyle}>Email *</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                required
                style={formInputStyle}
                placeholder="your.email@example.com"
              />
            </div>
            
            <div style={formGroupStyle}>
              <label style={formLabelStyle}>Message *</label>
              <textarea
                name="message"
                value={formData.message}
                onChange={handleInputChange}
                required
                rows={5}
                style={{...formInputStyle, resize: 'vertical'}}
                placeholder="Tell us about your plumbing needs..."
              />
            </div>
            
            <button
              type="submit"
              disabled={isSubmitting}
              style={submitButtonStyle}
            >
              {isSubmitting ? 'Sending...' : 'Send Message'}
            </button>
            
            {submitMessage && (
              <div style={submitMessageStyle}>
                {submitMessage}
              </div>
            )}
          </form>
        </div>
      </section>

      {/* Footer */}
      <footer id="contact" style={footerStyle}>
        <div style={footerContentStyle}>
          <div style={footerSectionStyle}>
            <img src="/DandCLogo.png" alt="D&C Plumbing" style={footerLogoStyle} />
            <p>Professional plumbing services across Stockport and Manchester</p>
          </div>
          <div style={footerSectionStyle}>
            <h4>Contact</h4>
            <p>Phone: 07438312107</p>
            <p>Email: info@dandcplumbing.co.uk</p>
          </div>
          <div style={footerSectionStyle}>
            <h4>Follow Us</h4>
            <div style={socialLinksStyle}>
              <a href="https://www.facebook.com/profile.php?id=61571539384923" target="_blank" rel="noopener noreferrer">
                Facebook
              </a>
              <a href="https://twitter.com/dcplumbing3" target="_blank" rel="noopener noreferrer">
                Twitter
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

// Inline styles for modern design
const headerStyle: React.CSSProperties = {
  background: 'white',
  color: '#333',
  padding: '2rem 0',
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  zIndex: 1000,
  boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
};

const navStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  maxWidth: '1200px',
  margin: '0 auto',
  padding: '0 2rem'
};

const logoContainerStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center'
};

const logoStyle: React.CSSProperties = {
  height: '120px',
  width: 'auto'
};

const navListStyle: React.CSSProperties = {
  display: 'flex',
  listStyle: 'none',
  gap: '2rem',
  margin: 0,
  padding: 0
};

const navLinkStyle: React.CSSProperties = {
  color: '#333',
  textDecoration: 'none',
  fontWeight: '500',
  transition: 'color 0.3s ease'
};

const heroStyle: React.CSSProperties = {
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  color: 'white',
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  textAlign: 'center',
  padding: '10rem 2rem 2rem',
  marginTop: '0'
};

const heroContentStyle: React.CSSProperties = {
  maxWidth: '800px'
};

const heroTitleStyle: React.CSSProperties = {
  fontSize: '3.5rem',
  fontWeight: '700',
  marginBottom: '1rem',
  textShadow: '2px 2px 4px rgba(0,0,0,0.3)'
};

const heroSubtitleStyle: React.CSSProperties = {
  fontSize: '1.5rem',
  marginBottom: '2rem',
  opacity: 0.9
};

const heroButtonsStyle: React.CSSProperties = {
  display: 'flex',
  gap: '1rem',
  justifyContent: 'center',
  flexWrap: 'wrap'
};

const primaryButtonStyle: React.CSSProperties = {
  background: 'linear-gradient(45deg, #ff6b6b, #ee5a24)',
  color: 'white',
  border: 'none',
  padding: '1rem 2rem',
  fontSize: '1.1rem',
  fontWeight: '600',
  borderRadius: '50px',
  cursor: 'pointer',
  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  boxShadow: '0 4px 15px rgba(255,107,107,0.3)'
};

const secondaryButtonStyle: React.CSSProperties = {
  background: 'transparent',
  color: 'white',
  border: '2px solid white',
  padding: '1rem 2rem',
  fontSize: '1.1rem',
  fontWeight: '600',
  borderRadius: '50px',
  cursor: 'pointer',
  transition: 'all 0.3s ease'
};

const contactBarStyle: React.CSSProperties = {
  background: '#f8f9fa',
  padding: '3rem 2rem',
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
  gap: '2rem',
  maxWidth: '1200px',
  margin: '0 auto'
};

const contactItemStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '1rem'
};

const contactIconStyle: React.CSSProperties = {
  fontSize: '2rem',
  background: 'linear-gradient(45deg, #667eea, #764ba2)',
  color: 'white',
  padding: '1rem',
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center'
};

const contactTitleStyle: React.CSSProperties = {
  margin: '0 0 0.5rem 0',
  fontSize: '1.2rem',
  fontWeight: '600'
};

const contactTextStyle: React.CSSProperties = {
  margin: 0,
  color: '#666'
};

const servicesStyle: React.CSSProperties = {
  padding: '4rem 2rem',
  background: 'white'
};

const sectionTitleStyle: React.CSSProperties = {
  fontSize: '2.5rem',
  fontWeight: '700',
  textAlign: 'center',
  marginBottom: '3rem',
  background: 'linear-gradient(45deg, #667eea, #764ba2)',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent'
};

const servicesGridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
  gap: '2rem',
  maxWidth: '1200px',
  margin: '0 auto'
};

const serviceCardStyle: React.CSSProperties = {
  background: 'white',
  padding: '2rem',
  borderRadius: '20px',
  textAlign: 'center',
  boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
  transition: 'transform 0.3s ease, box-shadow 0.3s ease'
};

const serviceIconStyle: React.CSSProperties = {
  fontSize: '3rem',
  marginBottom: '1rem'
};

const serviceCardTitleStyle: React.CSSProperties = {
  fontSize: '1.5rem',
  fontWeight: '600',
  marginBottom: '1rem',
  color: '#333'
};

const serviceCardTextStyle: React.CSSProperties = {
  color: '#666',
  lineHeight: '1.6'
};

const aboutStyle: React.CSSProperties = {
  background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
  padding: '4rem 2rem'
};

const aboutContentStyle: React.CSSProperties = {
  maxWidth: '800px',
  margin: '0 auto',
  textAlign: 'center'
};

const aboutTextStyle: React.CSSProperties = {
  fontSize: '1.1rem',
  lineHeight: '1.8',
  color: '#555',
  marginBottom: '1.5rem'
};

const areaListStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
  gap: '0.5rem',
  listStyle: 'none',
  padding: 0,
  margin: '2rem 0',
  fontSize: '0.9rem',
  color: '#666'
};

const footerStyle: React.CSSProperties = {
  background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
  color: 'white',
  padding: '3rem 2rem 1rem'
};

const footerContentStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
  gap: '2rem',
  maxWidth: '1200px',
  margin: '0 auto'
};

const footerSectionStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '1rem',
  alignItems: 'flex-start'
};

const footerLogoStyle: React.CSSProperties = {
  height: '80px',
  width: 'auto',
  marginBottom: '1rem',
  maxWidth: '200px',
  borderRadius: '10px'
};

const socialLinksStyle: React.CSSProperties = {
  display: 'flex',
  gap: '1rem'
};

// Recent Projects Styles
const projectsStyle: React.CSSProperties = {
  padding: '4rem 2rem',
  background: '#f8f9fa'
};

const projectsGridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
  gap: '2rem',
  maxWidth: '1200px',
  margin: '0 auto'
};

const projectCardStyle: React.CSSProperties = {
  background: 'white',
  borderRadius: '15px',
  overflow: 'hidden',
  boxShadow: '0 8px 25px rgba(0,0,0,0.1)',
  transition: 'transform 0.3s ease, box-shadow 0.3s ease'
};

const projectImageStyle: React.CSSProperties = {
  width: '100%',
  height: '250px',
  objectFit: 'contain',
  backgroundColor: '#f8f9fa',
  border: 'none',
  padding: '10px'
};

const projectContentStyle: React.CSSProperties = {
  padding: '1.5rem'
};

const projectTitleStyle: React.CSSProperties = {
  fontSize: '1.3rem',
  fontWeight: '600',
  marginBottom: '0.5rem',
  color: '#333'
};

const projectDescStyle: React.CSSProperties = {
  color: '#666',
  lineHeight: '1.6',
  fontSize: '0.9rem'
};

// Fixed Price Repairs Styles
const pricingStyle: React.CSSProperties = {
  padding: '4rem 2rem',
  background: 'white'
};

const pricingSubtitleStyle: React.CSSProperties = {
  textAlign: 'center',
  fontSize: '1.2rem',
  color: '#666',
  marginBottom: '3rem',
  maxWidth: '600px',
  margin: '0 auto 3rem'
};

const pricingGridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
  gap: '2rem',
  maxWidth: '1200px',
  margin: '0 auto'
};

const pricingCardStyle: React.CSSProperties = {
  background: '#f8f9fa',
  padding: '2rem',
  borderRadius: '15px',
  textAlign: 'center',
  border: '2px solid #e9ecef',
  transition: 'all 0.3s ease'
};

const pricingIconStyle: React.CSSProperties = {
  fontSize: '2.5rem',
  marginBottom: '1rem'
};

const pricingTitleStyle: React.CSSProperties = {
  fontSize: '1.3rem',
  fontWeight: '600',
  marginBottom: '1rem',
  color: '#333'
};

const priceStyle: React.CSSProperties = {
  fontSize: '1.8rem',
  fontWeight: '700',
  background: 'linear-gradient(45deg, #667eea, #764ba2)',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  marginBottom: '1.5rem'
};

const pricingListStyle: React.CSSProperties = {
  listStyle: 'none',
  padding: 0,
  margin: 0,
  textAlign: 'left'
};

const pricingNoteStyle: React.CSSProperties = {
  textAlign: 'center',
  marginTop: '3rem',
  padding: '2rem',
  background: '#e3f2fd',
  borderRadius: '10px',
  maxWidth: '800px',
  margin: '3rem auto 0'
};

// Credentials Section Styles
const credentialsStyle: React.CSSProperties = {
  padding: '4rem 2rem',
  background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)'
};

const credentialsSubtitleStyle: React.CSSProperties = {
  textAlign: 'center',
  fontSize: '1.2rem',
  color: '#666',
  marginBottom: '3rem',
  maxWidth: '600px',
  margin: '0 auto 3rem'
};

const credentialsGridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
  gap: '2rem',
  maxWidth: '1000px',
  margin: '0 auto'
};

const credentialCardStyle: React.CSSProperties = {
  background: 'white',
  padding: '2rem',
  borderRadius: '15px',
  textAlign: 'center',
  boxShadow: '0 8px 25px rgba(0,0,0,0.1)',
  transition: 'transform 0.3s ease, box-shadow 0.3s ease'
};

const credentialLogoStyle: React.CSSProperties = {
  height: '80px',
  width: 'auto',
  marginBottom: '1.5rem',
  maxWidth: '150px'
};

const credentialTitleStyle: React.CSSProperties = {
  fontSize: '1.4rem',
  fontWeight: '600',
  marginBottom: '1rem',
  color: '#333'
};

const credentialDescStyle: React.CSSProperties = {
  color: '#666',
  lineHeight: '1.6',
  fontSize: '0.95rem'
};

// Hamburger Menu Styles
const hamburgerStyle: React.CSSProperties = {
  display: 'none',
  flexDirection: 'column',
  justifyContent: 'space-around',
  width: '30px',
  height: '30px',
  background: 'transparent',
  border: 'none',
  cursor: 'pointer',
  padding: '0',
  zIndex: 1001
};

const hamburgerLineStyle: React.CSSProperties = {
  width: '100%',
  height: '3px',
  background: '#333',
  borderRadius: '3px',
  transition: 'all 0.3s ease',
  transformOrigin: 'center'
};

const mobileMenuOverlayStyle: React.CSSProperties = {
  position: 'fixed',
  top: '0',
  left: '0',
  right: '0',
  bottom: '0',
  background: 'rgba(255, 255, 255, 0.98)',
  backdropFilter: 'blur(10px)',
  zIndex: 1000,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  animation: 'fadeIn 0.3s ease-in-out'
};

const mobileMenuListStyle: React.CSSProperties = {
  listStyle: 'none',
  padding: 0,
  margin: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: '2rem',
  textAlign: 'center'
};

const mobileMenuLinkStyle: React.CSSProperties = {
  color: '#333',
  textDecoration: 'none',
  fontSize: '1.5rem',
  fontWeight: '600',
  padding: '1rem',
  borderRadius: '10px',
  transition: 'all 0.3s ease',
  display: 'block'
};

// Contact Form Styles
const contactFormSectionStyle: React.CSSProperties = {
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  padding: '4rem 2rem',
  color: 'white'
};

const contactFormContainerStyle: React.CSSProperties = {
  maxWidth: '800px',
  margin: '0 auto',
  textAlign: 'center'
};

const contactFormSubtitleStyle: React.CSSProperties = {
  fontSize: '1.2rem',
  marginBottom: '3rem',
  opacity: 0.9
};

const contactFormStyle: React.CSSProperties = {
  background: 'rgba(255, 255, 255, 0.1)',
  backdropFilter: 'blur(10px)',
  padding: '3rem',
  borderRadius: '20px',
  boxShadow: '0 15px 40px rgba(0,0,0,0.2)'
};

const formRowStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: '1.5rem',
  marginBottom: '1.5rem'
};

const formGroupStyle: React.CSSProperties = {
  marginBottom: '1.5rem',
  textAlign: 'left'
};

const formLabelStyle: React.CSSProperties = {
  display: 'block',
  marginBottom: '0.5rem',
  fontWeight: '600',
  color: 'white'
};

const formInputStyle: React.CSSProperties = {
  width: '100%',
  padding: '1rem',
  border: '2px solid rgba(255,255,255,0.2)',
  borderRadius: '10px',
  background: 'rgba(255,255,255,0.1)',
  color: 'white',
  fontSize: '1rem',
  transition: 'all 0.3s ease',
  backdropFilter: 'blur(5px)'
};

const submitButtonStyle: React.CSSProperties = {
  background: 'linear-gradient(45deg, #ff6b6b, #ee5a24)',
  color: 'white',
  border: 'none',
  padding: '1rem 3rem',
  fontSize: '1.1rem',
  fontWeight: '600',
  borderRadius: '50px',
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  boxShadow: '0 4px 15px rgba(255,107,107,0.3)',
  marginTop: '1rem'
};

const submitMessageStyle: React.CSSProperties = {
  marginTop: '1rem',
  padding: '1rem',
  background: 'rgba(255,255,255,0.2)',
  borderRadius: '10px',
  textAlign: 'center'
};

export default App;
